import { useEffect, useMemo, useState } from "react";
import { duplicateBacktest } from "@/lib/duplicate-backtest";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { ActionsMenu } from "../../../components/ActionsMenu";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { Card, ExchangeMark, Pill } from "../../../components/ui/kit";
import { Pager } from "../../../components/ui/Pager";
import { clampPage } from "@/lib/paging";
import { useApi } from "../../../context/ApiContext";
import type { Backtest } from "@/api/types";
import { isLabInstance, liveInstancesFor, scriptLabel } from "@/lib/live";
import { FilterBar } from "@/components/ui/FilterDialog";
import { providerLabel } from "@/lib/account";
import { storedSummary } from "@/lib/backtest-summary";
import { useFormatDate } from "@/hooks/useFormatDate";
import { useUiStore } from "@/stores/ui";
import { pageSizeFor, useGridColumns } from "@/hooks/useGridColumns";

type Filter = "all" | "live" | "notlive";
type Sort = "updated" | "pnl" | "winrate";


const SORTS: { id: Sort; label: string }[] = [
  { id: "updated", label: "Recently updated" },
  { id: "pnl", label: "Best P&L" },
  { id: "winrate", label: "Best win rate" },
];
const stat = "text-[10px] uppercase tracking-[0.06em] text-[var(--text-muted)]";
const statValue = "num mt-0.5 text-[15px] font-semibold text-[var(--text-primary)]";
const money = (n: number) => `${n >= 0 ? "+" : "−"}$${Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2 })}`;
const pct = (n: number) => `${n >= 0 ? "+" : "−"}${Math.abs(n).toFixed(1)}%`;

export default function BacktestList() {
  const api = useApi(["backtests", "scripts"]);
  const { date } = useFormatDate();
  const navigate = useNavigate();
  const openNewBacktest = useUiStore((st) => st.openNewBacktest);
  const openGoLive = useUiStore((st) => st.openGoLive);
  const [params] = useSearchParams();
  const [q, setQ] = useState(() => params.get("q") ?? "");
  // Global search links here with ?q=SYMBOL.
  useEffect(() => {
    const next = params.get("q");
    if (next != null) setQ(next);
  }, [params]);
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<Sort>("updated");
  const [exchange, setExchange] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [page, setPage] = useState(0);

  const backtests: Backtest[] = useMemo(() => api.backtests || [], [api.backtests]);

  // Each lab backtest with the state of its live copies.
  const rows = useMemo(
    () =>
      backtests
        .filter(isLabInstance)
        .sort((a, b) => Date.parse(b.updated_at || "") - Date.parse(a.updated_at || ""))
        .map((bt) => {
          const live = liveInstancesFor(bt, backtests);
          // "Live" means a copy is switched on now; a stopped copy counts as not live.
          const active = live.filter((l) => l.enabled && l.status !== "stopped");
          return { bt, live: active.length > 0, liveId: active[0]?.id, sum: storedSummary(bt.metadata) };
        }),
    [backtests]
  );

  const counts = useMemo(
    () => ({ all: rows.length, live: rows.filter((r) => r.live).length, notlive: rows.filter((r) => !r.live).length }),
    [rows]
  );

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = rows.filter(({ bt, live }) => {
      if (filter === "live" && !live) return false;
      if (filter === "notlive" && live) return false;
      if (exchange && bt.market_provider !== exchange) return false;
      if (!needle) return true;
      return (
        bt.name?.toLowerCase().includes(needle) ||
        bt.symbol?.toLowerCase().includes(needle) ||
        scriptLabel(bt, api.scripts).toLowerCase().includes(needle)
      );
    });
    // Backtests not run since (no summary) go last when sorting by results.
    if (sort === "pnl") return [...out].sort((a, b) => (b.sum?.pnl ?? -Infinity) - (a.sum?.pnl ?? -Infinity));
    if (sort === "winrate")
      return [...out].sort(
        (a, b) => (b.sum?.trades ? b.sum.win_rate : -1) - (a.sum?.trades ? a.sum.win_rate : -1)
      );
    return out;
  }, [rows, filter, q, sort, exchange, api.scripts]);
  // A new search, filter or order starts again from the first page.
  useEffect(() => setPage(0), [filter, q, sort, exchange]);
  // The exchanges backtests use market data from, for the exchange filter.
  const exchanges = useMemo(
    () => [...new Set(rows.map((r) => r.bt.market_provider).filter((p): p is string => Boolean(p)))].sort(),
    [rows]
  );
  // Compact cards in whole rows: a page is a few rows of however many fit across.
  const [gridRef, cols] = useGridColumns(4);
  const PAGE_SIZE = pageSizeFor(cols);
  const lastPage = Math.max(0, Math.ceil(items.length / PAGE_SIZE) - 1);
  const safePage = clampPage(page, PAGE_SIZE, items.length);
  const visible = items.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  // The "New backtest" card only takes a free spot on the last page.
  const roomForNew = visible.length < PAGE_SIZE;

  async function handleDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    setError(null);
    try {
      await api.commands.deleteBacktest({ id: deleteTarget.id });
      setDeleteTarget(null);
    } catch (e: unknown) {
      setError((e instanceof Error && e.message) || "Couldn't delete the backtest. Try again.");
    } finally {
      setBusyId(null);
    }
  }

  async function duplicate(bt: Backtest) {
    setBusyId(bt.id);
    setError(null);
    try {
      const created = await duplicateBacktest(api.commands, bt, api.scripts || []);
      navigate(`/lab/backtest/${created.id}`, { state: { syncedBacktest: created } });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Couldn't duplicate the backtest. Try again.");
    } finally {
      setBusyId(null);
    }
  }

  const FILTERS: { id: Filter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "live", label: "Live" },
    { id: "notlive", label: "Not live" },
  ];

  return (
    <div className="flex flex-col gap-5">
      {/* No page title: the menu says where you are, so the tools take that row. */}
      <h1 className="sr-only">Backtests</h1>
      {error && (
        <div role="alert" className="rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-sm text-[var(--danger)]">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <label className="flex min-w-[220px] items-center gap-2 rounded-[10px] border border-[var(--border)] bg-[var(--bg-secondary)] px-2.5 py-1.5 text-[13px] text-[var(--text-disabled)] focus-within:border-[var(--primary)]">
          <Search size={14} className="shrink-0" />
          <input
            id="backtest-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or coin"
            aria-label="Search backtests"
            className="!h-auto !min-h-0 w-full !border-0 !bg-transparent !p-0 text-[13px] text-[var(--text-primary)] !shadow-none !outline-none"
          />
        </label>
        <FilterBar
          title="Filter backtests"
          shown={items.length}
          groups={[
            {
              id: "status",
              label: "Live",
              value: filter,
              defaultValue: "all",
              options: FILTERS.map((f) => ({ value: f.id, label: f.label, count: counts[f.id] })),
            },
            ...(exchanges.length > 1
              ? [
                  {
                    id: "exchange",
                    label: "Exchange",
                    value: exchange,
                    defaultValue: "",
                    options: [
                      { value: "", label: "All exchanges" },
                      ...exchanges.map((p) => ({ value: p, label: providerLabel(p), count: rows.filter((r) => r.bt.market_provider === p).length })),
                    ],
                  },
                ]
              : []),
            { id: "sort", label: "Sort by", value: sort, defaultValue: "updated", quiet: true, options: SORTS.map((o) => ({ value: o.id, label: o.label })) },
          ]}
          onChange={(id, value) => {
            if (id === "status") setFilter(value as Filter);
            else if (id === "exchange") setExchange(value);
            else setSort(value as Sort);
          }}
          onClear={() => {
            setFilter("all");
            setExchange("");
          }}
        />
        <button type="button" onClick={openNewBacktest} className="btn btn-primary">
          + New backtest
        </button>
      </div>

      {!api.ready ? (
        <Card className="px-4 py-8 text-center text-[13px] text-[var(--text-muted)]">Loading…</Card>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 px-4 py-12 text-center">
          <b className="text-[15px] text-[var(--text-primary)]">No backtests yet</b>
          <button type="button" className="btn btn-primary" onClick={openNewBacktest}>
            Create your first one
          </button>
        </Card>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-2.5 px-4 py-10 text-center text-[13px] text-[var(--text-muted)]">
          Nothing matches these filters.
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setQ("");
              setFilter("all");
              setExchange("");
            }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <>
          <div ref={gridRef} className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(250px,1fr))]">
            {visible.map(({ bt, live, liveId, sum }) => (
              // The last run's numbers, saved on the backtest when it ran; none until it runs again.
              <section
                key={bt.id}
                aria-label={bt.name}
                className="flex min-w-0 flex-col gap-3 rounded-card border border-[var(--border)] bg-[var(--bg-card)] p-3.5"
              >
                <div className="flex items-start gap-2.5">
                  <ExchangeMark provider={bt.market_provider} size={28} />
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/lab/backtest/${bt.id}`}
                      className="block truncate text-[14px] font-semibold text-[var(--text-primary)] no-underline hover:underline"
                    >
                      {bt.name}
                    </Link>
                    <div className="truncate text-xs text-[var(--text-muted)]">
                      {bt.symbol} · {bt.timeframe}
                    </div>
                  </div>
                  {live && (
                    <span className="shrink-0">
                      <Pill tone="ok">Live</Pill>
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div title="From the last run of this period">
                    <div className={stat}>P&amp;L</div>
                    <div
                      className={statValue}
                      style={sum ? { color: sum.pnl >= 0 ? "var(--success)" : "var(--danger)" } : { color: "var(--text-muted)" }}
                    >
                      {sum ? money(sum.pnl) : "—"}
                    </div>
                    {sum && <div className="num text-xs text-[var(--text-muted)]">{pct(sum.pnl_pct)}</div>}
                  </div>
                  <div title="Share of closed trades that made money">
                    <div className={stat}>Win rate</div>
                    <div className={statValue}>
                      {sum && sum.trades > 0 ? `${(sum.win_rate * 100).toFixed(0)}%` : <span className="text-[var(--text-muted)]">—</span>}
                    </div>
                    {sum && sum.trades > 0 ? (
                      <div className="num text-xs text-[var(--text-muted)]">
                        {Math.round(sum.win_rate * sum.trades)}/{sum.trades} won
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="truncate text-[11px] text-[var(--text-muted)]">
                  {sum ? (
                    <span className="num">
                      {bt.start_date ? date(bt.start_date) : "—"} → {bt.end_date ? date(bt.end_date) : "now"}
                    </span>
                  ) : (
                    "No results saved yet. Open it to run."
                  )}
                </div>

                <div className="mt-auto flex items-center gap-2">
                  <Link to={`/lab/backtest/${bt.id}`} className="btn btn-primary btn-sm flex-1">
                    Open
                  </Link>
                  {live && liveId ? (
                    <Link to={`/live/${liveId}`} className="btn btn-soft btn-sm flex-1">
                      View live
                    </Link>
                  ) : (
                    <button type="button" className="btn btn-soft btn-sm flex-1" onClick={() => openGoLive(bt.id)}>
                      Go live
                    </button>
                  )}
                  <ActionsMenu
                    items={[
                      { label: "Duplicate", disabled: busyId === bt.id, onClick: () => void duplicate(bt) },
                      { label: "Delete", danger: true, onClick: () => setDeleteTarget({ id: bt.id, name: bt.name }) },
                    ]}
                  />
                </div>
              </section>
            ))}
            {filter !== "live" && !q && !exchange && safePage === lastPage && roomForNew && (
              <button
                type="button"
                onClick={openNewBacktest}
                className="flex min-h-[150px] flex-col items-center justify-center gap-1.5 rounded-card border border-dashed border-[var(--border)] p-3.5 text-center text-[var(--text-secondary)] hover:border-[var(--primary)] hover:bg-[var(--bg-hover)]"
              >
                <Plus size={22} aria-hidden />
                <span className="text-[14px] font-semibold text-[var(--text-primary)]">New backtest</span>
                <span className="text-xs text-[var(--text-muted)]">Paste a TradingView strategy or start from a popular one.</span>
              </button>
            )}
          </div>
          <Pager className="" page={safePage} pageSize={PAGE_SIZE} total={items.length} onPage={setPage} />
        </>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Delete "${deleteTarget?.name}"?`}
        description="This permanently removes the backtest and its settings."
        loading={busyId === deleteTarget?.id}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
